/* eslint-disable no-misleading-character-class */
export const SEARCH_DELAY = 300;
export const AUTOCOMPLETE_RECENT_ITEMS_COUNT = 3;
export const AUTOCOMPLETE_LIMIT_TAGS = 3;
export const ACCORDION_DATA_ATTRIBUTE_KEY = 'data-filter-name';
export const AUTOCOMPLETE_SEARCH_TRIGGER_DEFAULT_MIN_LENGTH = 1;

/**
 * People keyword-source selection limit (people search only). The backend
 * counterpart enforces this server-side; see ClickUp CU-86e23vpwd / 86e23vyqr.
 */
export const PEOPLE_KEYWORD_SOURCES_MAX_SELECTED = 5;

/**
 * Unicode character classes supporting international characters for URL validation
 *
 * Supported character sets:
 * • ASCII: a-z, A-Z, 0-9, underscore (_), hyphen (-), period (.), ampersand (&), apostrophe ('), quotation marks ("), parentheses (()), Chinese parentheses （）, em dash (–), Turkish İ (U+0130), Turkish ı (U+0131)
 * • Latin Extended (U+00C0-U+017F): àáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿ
 * • Latin Extended-A (U+0100-U+017F): Āā, Ēē, Īī, Ōō, Ūū
 * • Latin Extended-B (U+0180-U+024F): ƒ, ǽ, ȳ, etc.
 * • Latin Extended Additional (U+1E00-U+1EFF): ḃ, ḋ, ḟ, etc.
 * • Cyrillic (U+0400-U+04FF): АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя
 * • Arabic (U+0600-U+06FF): العربية، فارسی، اردو
 * • Greek (U+0370-U+03FF): ΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩαβγδεζηθικλμνξοπρστυφχψω
 * • Hebrew (U+0590-U+05FF): עברית
 * • Thai (U+0E00-U+0E7F): ไทย
 * • Lao (U+0E80-U+0EFF): ລາວ
 * • Devanagari (U+0900-U+097F): हिन्दी, संस्कृत
 * • Hangul (U+AC00-U+D7AF): 한글
 * • CJK Unified Ideographs (U+4E00-U+9FFF): 中文, 日本語, 한국어
 * • Hiragana (U+3040-U+309F): ひらがな (Japanese)
 * • Katakana (U+30A0-U+30FF): カタカナ (Japanese)
 * • Armenian (U+0530-U+058F): հայերեն
 * • Georgian (U+10A0-U+10FF): ქართული
 * • Ethiopic (U+1200-U+137F): አማርኛ (Amharic)
 * • Telugu (U+0C00-U+0C7F): తెలుగు
 * • Vietnamese Latin Extended Additional (U+1EA0-U+1EF9): Vietnamese characters with diacritics (ằ, ầ, ẩ, ẫ, ậ, etc.)
 * • Vietnamese D characters (U+0110, U+0111): Đ (capital D with stroke), đ (lowercase d with stroke)
 * • Latin characters with grave accent (U+00EC): ì (i with grave accent)
 * • Combining Diacritical Marks (U+0300-U+036F): Combining accents and diacritical marks (̀, ́, ̂, ̃, etc.)
 * • Latin-1 Supplement (U+00A0-U+00FF): Extended Latin characters including accented characters (á, ó, í, ô, ç, etc.)
 * • General Punctuation (U+2000-U+206F): Various punctuation marks
 * • CJK Symbols and Punctuation (U+3000-U+303F): Chinese punctuation marks
 * • Halfwidth and Fullwidth Forms (U+FF00-U+FFEF): Full-width characters
 * • BMP symbol/dingbat emoji (U+2100-U+27BF, U+2B00-U+2BFF): ♥, ✅, ☺, ⭐, ☀, arrows, etc.
 * • Combining marks for symbols & Variation Selectors (U+20D0-U+20FF, U+FE00-U+FE0F): keycap combiners and VS-16 emoji-presentation selector (❤️)
 * • Surrogate pairs (U+D800-U+DFFF): astral-plane characters such as emoji (🍪, 😀, 🎉, flags, skin-tone modifiers) that appear in LinkedIn slugs
 */
const CHARACTER_CLASS =
  '[a-zA-Z0-9_\\u00C0-\\u017F\\u0100-\\u017F\\u0180-\\u024F\\u1E00-\\u1EFF\\u0400-\\u04FF\\u0600-\\u06FF\\u0370-\\u03FF\\u0590-\\u05FF\\u0E00-\\u0E7F\\u0E80-\\u0EFF\\u0900-\\u097F\\uAC00-\\uD7AF\\u4E00-\\u9FFF\\u3040-\\u309F\\u30A0-\\u30FF\\u0530-\\u058F\\u10A0-\\u10FF\\u1200-\\u137F\\u0C00-\\u0C7F\\u00A0-\\u00FF\\u2000-\\u206F\\u3000-\\u303F\\uFF00-\\uFFEF\\u0130\\u0131\\u0110\\u0111\\u00EC\\u0300-\\u036F\\u1EA0-\\u1EF9\\u2100-\\u27BF\\u2B00-\\u2BFF\\u20D0-\\u20FF\\uFE00-\\uFE0F\\uD800-\\uDFFF\\-\\.&\\\'\\"\\(\\)（）–]';

const TLD_PATTERN =
  '[a-zA-Z0-9\\u00C0-\\u017F\\u0100-\\u017F\\u0180-\\u024F\\u1E00-\\u1EFF\\u0400-\\u04FF\\u0600-\\u06FF\\u0370-\\u03FF\\u0590-\\u05FF\\u0E00-\\u0E7F\\u0E80-\\u0EFF\\u0900-\\u097F\\uAC00-\\uD7AF\\u4E00-\\u9FFF\\u3040-\\u309F\\u30A0-\\u30FF\\u0530-\\u058F\\u10A0-\\u10FF\\u1200-\\u137F\\u00A0-\\u00FF]{2,10}';

export const companyLinkedinUrlRegexPatterns = [
  new RegExp('^linkedin\\.com/company/' + CHARACTER_CLASS + '+/?$'), // Matches "linkedin.com/company/apple" or "linkedin.com/company/apple/"
  new RegExp('^www\\.linkedin\\.com/company/' + CHARACTER_CLASS + '+/?$'), // Matches "www.linkedin.com/company/apple" or "www.linkedin.com/company/apple/"
  new RegExp('^https://linkedin\\.com/company/' + CHARACTER_CLASS + '+/?$'), // Matches "https://linkedin.com/company/apple" or "https://linkedin.com/company/apple/"
  new RegExp(
    '^https://www\\.linkedin\\.com/company/' + CHARACTER_CLASS + '+/?$'
  ), // Matches "https://www.linkedin.com/company/apple" or "https://www.linkedin.com/company/apple/"
  new RegExp('^http://linkedin\\.com/company/' + CHARACTER_CLASS + '+/?$'), // Matches "http://linkedin.com/company/apple" or "http://linkedin.com/company/apple/"
  new RegExp(
    '^http://www\\.linkedin\\.com/company/' + CHARACTER_CLASS + '+/?$'
  ), // Matches "http://www.linkedin.com/company/apple" or "http://www.linkedin.com/company/apple/"
];

export const peopleLinkedinUrlRegexPatterns = [
  new RegExp('^linkedin\\.com/in/' + CHARACTER_CLASS + '+/?$'), // Matches "linkedin.com/in/nathanlatka" or "linkedin.com/in/nathanlatka/"
  new RegExp('^www\\.linkedin\\.com/in/' + CHARACTER_CLASS + '+/?$'), // Matches "www.linkedin.com/in/nathanlatka" or "www.linkedin.com/in/nathanlatka/"
  new RegExp('^https://linkedin\\.com/in/' + CHARACTER_CLASS + '+/?$'), // Matches "https://linkedin.com/in/nathanlatka" or "https://linkedin.com/in/nathanlatka/"
  new RegExp('^https://www\\.linkedin\\.com/in/' + CHARACTER_CLASS + '+/?$'), // Matches "https://www.linkedin.com/in/nathanlatka" or "https://www.linkedin.com/in/nathanlatka/"
  new RegExp('^http://linkedin\\.com/in/' + CHARACTER_CLASS + '+/?$'), // Matches "http://linkedin.com/in/nathanlatka" or "http://linkedin.com/in/nathanlatka/"
  new RegExp('^http://www\\.linkedin\\.com/in/' + CHARACTER_CLASS + '+/?$'), // Matches "http://www.linkedin.com/in/nathanlatka" or "http://www.linkedin.com/in/nathanlatka/"
];

export const schoolLinkedinUrlRegexPatterns = [
  new RegExp('^linkedin\\.com/school/' + CHARACTER_CLASS + '+/?$'), // Matches "linkedin.com/school/university-name" or "linkedin.com/school/university-name/"
  new RegExp('^www\\.linkedin\\.com/school/' + CHARACTER_CLASS + '+/?$'), // Matches "www.linkedin.com/school/university-name" or "www.linkedin.com/school/university-name/"
  new RegExp('^https://linkedin\\.com/school/' + CHARACTER_CLASS + '+/?$'), // Matches "https://linkedin.com/school/university-name" or "https://linkedin.com/school/university-name/"
  new RegExp(
    '^https://www\\.linkedin\\.com/school/' + CHARACTER_CLASS + '+/?$'
  ), // Matches "https://www.linkedin.com/school/university-name" or "https://www.linkedin.com/school/university-name/"
  new RegExp('^http://linkedin\\.com/school/' + CHARACTER_CLASS + '+/?$'), // Matches "http://linkedin.com/school/university-name" or "http://linkedin.com/school/university-name/"
  new RegExp('^http://www\\.linkedin\\.com/school/' + CHARACTER_CLASS + '+/?$'), // Matches "http://www.linkedin.com/school/university-name" or "http://www.linkedin.com/school/university-name/"
];

export const companyDomainRegexPatterns = [
  new RegExp('^' + CHARACTER_CLASS + '+\\.' + TLD_PATTERN + '.*'), // Matches "amazon.com", "ai-bees.io", etc. with any path/query/fragment
  new RegExp('^www\\.' + CHARACTER_CLASS + '+\\.' + TLD_PATTERN + '.*'), // Matches "www.apple.com", "www.ai-ark.com", etc. with any path/query/fragment
  new RegExp('^https://' + CHARACTER_CLASS + '+\\.' + TLD_PATTERN + '.*'), // Matches "https://ai-bees.io", "https://amazon.com", etc. with any path/query/fragment
  new RegExp('^https://www\\.' + CHARACTER_CLASS + '+\\.' + TLD_PATTERN + '.*'), // Matches "https://www.ai-ark.com", "https://www.apple.com", etc. with any path/query/fragment
  new RegExp('^http://' + CHARACTER_CLASS + '+\\.' + TLD_PATTERN + '.*'), // Matches "http://ai-bees.io", "http://amazon.com", etc. with any path/query/fragment
  new RegExp('^http://www\\.' + CHARACTER_CLASS + '+\\.' + TLD_PATTERN + '.*'), // Matches "http://www.ai-ark.com", "http://www.apple.com", etc. with any path/query/fragment
  new RegExp(
    '^' + CHARACTER_CLASS + '+@' + CHARACTER_CLASS + '+\\.' + TLD_PATTERN + '.*'
  ), // Matches "info@bet.com", "contact@amazon.com", etc. with any path/query/fragment
];

export const companyNameRegexPatterns = [
  /.+/, // Matches any non-empty string
];
